import { useEffect, useState } from "react";
import { useQuery } from "urql";
import { GET_ALL_ORDERS, GET_ORDERS } from "@/gql/queries";
import { dateRange } from "@/utils/date";
import { OrderSubGraphType } from "@/types/orders";

interface OrdersCollection {
  orders_collection: OrderSubGraphType[];
}

const itemsPerPage = 1000;

const useAllOrdersPooling = (address: string) => {
  const [page, setPage] = useState<number>(0);
  const [allOrders, setAllOrders] = useState<OrderSubGraphType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [{ data, fetching, error }, reexecuteQuery] =
    useQuery<OrdersCollection>({
      query: GET_ORDERS,
      variables: {
        first: itemsPerPage,
        skip: page * itemsPerPage,
        placedAtGte: dateRange._gte,
        placedAtLte: dateRange._lte,
        merchantAcceptedAddr: address,
      },
      pause: !loading,
    });

  useEffect(() => {
    setAllOrders([]);
  }, []);

  useEffect(() => {
    if (data?.orders_collection?.length === 0) {
      setLoading(false);
    }
    if (
      !fetching &&
      data?.orders_collection &&
      data?.orders_collection?.length > 0
    ) {
      setAllOrders((prevOrders) => [
        ...prevOrders,
        ...(data.orders_collection || []),
      ]);
      setPage((prevPage) => prevPage + 1);
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  }, [page, fetching, data, address]);

  return {
    loading,
    error,
    orders: allOrders,
  };
};

const segregateOrdersByMonth = (
  orders: OrderSubGraphType[],
): Record<string, OrderSubGraphType[]> => {
  const correctedResult: Record<string, OrderSubGraphType[]> = {};
  orders.forEach((order) => {
    const placedAt = new Date(parseInt(order.placed_at) * 1000);
    const yearMonth = `${placedAt.getFullYear()}-${String(placedAt.getMonth() + 1).padStart(2, "0")}`;
    if (!correctedResult[yearMonth]) {
      correctedResult[yearMonth] = [];
    }
    correctedResult[yearMonth].push(order);
  });

  return correctedResult;
};

const useAllOrdersByMonthPooling = (address: string) => {
  const [page, setPage] = useState<number>(0);
  const [allOrders, setAllOrders] = useState<OrderSubGraphType[]>([]);
  const [segregatedOrders, setSegregatedOrders] = useState<
    Record<string, OrderSubGraphType[]>
  >({});

  const [loading, setLoading] = useState<boolean>(true);

  const [{ data, fetching, error }, reexecuteQuery] =
    useQuery<OrdersCollection>({
      query: GET_ALL_ORDERS,
      variables: {
        first: itemsPerPage,
        skip: page * itemsPerPage,
        merchantAcceptedAddr: address,
      },
      pause: !loading,
    });

  useEffect(() => {
    setAllOrders([]);
  }, []);

  useEffect(() => {
    if (data?.orders_collection?.length === 0) {
      setLoading(false);
      setSegregatedOrders(segregateOrdersByMonth(allOrders));
    }

    if (
      !fetching &&
      data?.orders_collection &&
      data?.orders_collection?.length > 0
    ) {
      setAllOrders((prevOrders) => [
        ...prevOrders,
        ...(data.orders_collection || []),
      ]);
      setPage((prevPage) => prevPage + 1);
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  }, [page, fetching, data, allOrders, address]);

  return {
    loading,
    error,
    orders: allOrders,
    segregatedOrders,
  };
};

export { useAllOrdersPooling, useAllOrdersByMonthPooling };
